import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ManhwaCard from '@/components/base/ManhwaCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { usePageMeta } from '@/hooks/usePageMeta';
import { featuredManhwa } from '@/mocks/manhwa';

type ManhwaItem = (typeof featuredManhwa)[number];

const categoryToSlug: Record<string, string> = {
  '奇幻冒险': 'fantasy',
  '浪漫爱情': 'romance',
  '悬疑惊悚': 'thriller',
  '都市职场': 'urban',
  '仙侠修真': 'cultivation',
  '治愈日常': 'healing',
  '武侠江湖': 'martial-arts',
  '科幻未来': 'sci-fi',
};

const articleDataMap: Record<string, ManhwaItem & { synopsis: string; relatedSlugs: string[] }> = {
  'sword-and-magic': {
    ...featuredManhwa[0] as any,
    synopsis: '在遥远的艾泽拉斯大陆，魔法与剑术是两大力量体系。少年艾伦出身于偏远的剑士村，偶然间获得了一把刻有远古符文的魔剑。从此，他踏上了一段充满危险与奇遇的冒险之旅。\n\n旅途中，艾伦结识了精灵魔法师艾琳娜、兽人战士格鲁和神秘的暗影刺客影。四人组成冒险小队，对抗试图唤醒远古魔王的黑暗教团。\n\n随着冒险深入，艾伦逐渐发现自己的身世之谜——他竟是千年前封印魔王的光明剑圣的后裔。而魔剑中沉睡的远古力量，正是封印魔王的关键。\n\n一场跨越千年的宿命之战即将展开，艾伦是否能继承先祖的意志，守护大陆的和平？',
    relatedSlugs: ['magic-academy', 'nine-tailed-fox', 'peach-blossom']
  },
  'sweet-trap': {
    ...featuredManhwa[1] as any,
    synopsis: '韩秀雅是个普通的职场新人，却因为一次意外，与公司最年轻帅气的CEO姜宇镇卷入了一场假扮恋人的协议。\n\n原本以为只是一场简单的交易，却在朝夕相处中，两人的感情逐渐升温。姜宇镇的温柔体贴让秀雅心动，但前任女友的出现、商业对手的阴谋，让这段感情充满变数。\n\n当真心与算计交织，甜蜜的陷阱中，谁来分辨真心？一个关于爱情、成长与信任的都市浪漫故事。',
    relatedSlugs: ['sword-and-magic', 'office-queen', 'tyrant-wife']
  },
  'night-tracker': {
    ...featuredManhwa[2] as any,
    synopsis: '首尔连续发生多起离奇命案，受害者之间没有任何关联，唯一共同点是都收到过一张黑色卡片。\n\n天才犯罪侧写师车敏俊从国外归来，与重案组刑警金泰浩联手调查此案。随着调查深入，他们发现这背后隐藏着一个横跨二十年的惊天秘密。\n\n\'追逐者\'——这个神秘的连环杀手，每次作案都会在现场留下线索，仿佛在邀请警方玩一场死亡游戏。\n\n当车敏俊发现自己与凶手之间竟有着不可思议的联系时，游戏规则彻底改变了。真相，往往比想象中更加黑暗。',
    relatedSlugs: ['apocalypse-survival', 'sword-and-magic', 'rebirth-business']
  },
  'rebirth-business': {
    ...featuredManhwa[3] as any,
    synopsis: '李正宇前世白手起家创办宇成集团，却因信任的合伙人与妻子的联手背叛，不仅失去一切，还含冤入狱，最终在狱中含恨而终。\n\n当他再次睁开眼，发现自己回到了二十年前——正是他大学毕业、准备创业的那一年。\n\n带着未来二十年的记忆，李正宇决心改写命运。他避开了曾经的陷阱，提前投资了后来成为巨头的科技公司，一步步建立起比前世更加庞大的商业帝国。\n\n但命运似乎在与他开玩笑——前世的仇人提前出现，新的竞争对手虎视眈眈。这一次，他不会再给任何人机会。',
    relatedSlugs: ['office-queen', 'sword-and-magic', 'sweet-trap']
  },
  'cultivation-simulator': {
    ...featuredManhwa[4] as any,
    synopsis: '普通大学生张浩在手机上下载了一款名为\'修仙模拟器\'的App后，生活发生了翻天覆地的变化。\n\n通过完成系统发布的任务——修炼、炼丹、猎妖——他一步步从凡人踏上了修仙之路。然而，灵气复苏的秘密逐渐揭开，原来这个世界并非表面看起来那么简单。\n\n在修真界，张浩结识了各路道友，也遭遇了心狠手辣的魔修。随着实力的提升，他逐渐意识到——修仙模拟器的背后，隐藏着一个关于末法时代的巨大秘密。',
    relatedSlugs: ['peach-blossom', 'nine-tailed-fox', 'sword-and-magic']
  },
  'cat-cafe': {
    ...featuredManhwa[5] as any,
    synopsis: '在首尔弘大附近的小巷深处，有一间名为\'慢慢\'的猫咪咖啡厅。店主尹素熙是个热爱猫咪的温柔女孩，而咖啡厅里的每一只猫咪，都有它们独特的故事。\n\n来这里的客人形形色色——失恋的大学生、疲惫的上班族、孤独的老人、好奇的孩子......在与猫咪们的相处中，每个人都在这里找到了片刻的安宁与治愈。\n\n这是一个关于温暖、治愈与生活中那些微小而美好的瞬间的故事。',
    relatedSlugs: ['sweet-trap', 'office-queen', 'sword-and-magic']
  },
  'tyrant-wife': {
    id: 7,
    title: '暴君的小娇妻',
    author: '郑秀雅',
    category: '浪漫爱情',
    slug: 'tyrant-wife',
    cover: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20romance%20illustration%20of%20a%20royal%20couple%20in%20historical%20palace%20setting%2C%20soft%20pink%20and%20gold%20tones%2C%20romantic%20webtoon%20cover%20art%20style%2C%20elegant%20atmosphere%2C%20clean%20composition&width=800&height=1120&seq=manhwa-cover-07&orientation=portrait',
    description: '公主李善雅被迫嫁给邻国以暴虐闻名的君王金泰俊，所有人都以为她将迎来悲惨的命运。然而，这位暴君面对她时却展现出意外的温柔。',
    rating: 8.8,
    chapters: 134,
    views: 2670000,
    updateDate: '2026-07-01',
    tags: ['宫廷', '爱情', '甜宠'],
    synopsis: '公主李善雅被迫嫁给邻国以暴虐闻名的君王金泰俊，所有人都以为她将迎来悲惨的命运。然而，这位\'暴君\'面对她时却展现出意外的温柔。\n\n原来，金泰俊的暴虐只是对外界的伪装，真实的他是一个内心柔软、渴望被爱的人。善雅用自己的善良和智慧，逐渐融化了君王的心，也在宫廷的权谋斗争中展现了非凡的胆识。\n\n当邻国的战争威胁来临，这对夫妻必须携手面对外敌与内鬼，守护他们的王国与爱情。',
    relatedSlugs: ['sweet-trap', 'sword-and-magic', 'peach-blossom']
  },
  'peach-blossom': {
    id: 12,
    title: '三生三世桃花开',
    author: '林秀晶',
    category: '仙侠修真',
    slug: 'peach-blossom',
    cover: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20cultivation%20romance%20illustration%20of%20a%20fairy%20couple%20under%20blooming%20peach%20trees%2C%20soft%20pink%20and%20jade%20green%20tones%2C%20dreamy%20webtoon%20cover%20art%20style&width=800&height=1120&seq=manhwa-cover-12&orientation=portrait',
    description: '三界轮回间，桃花仙子的爱恨情仇跨越千年时光，谱写一段凄美动人的仙侠恋歌。',
    rating: 9.1,
    chapters: 201,
    views: 5670000,
    updateDate: '2026-07-03',
    tags: ['仙侠', '爱情', '轮回'],
    synopsis: '三界轮回间，桃花仙子的爱恨情仇跨越千年时光。前世她是天界的桃花仙，因触犯天条被打入凡间轮回。今生她转世为普通少女，却意外唤醒了前世的记忆与法力。\n\n随着记忆复苏，她发现自己与两位男子之间有着跨越三世的纠葛——一位是默默守护她的上仙，另一位是曾为她倾覆三界的魔尊。\n\n命运的齿轮再次转动，这一世她能否打破轮回宿命？',
    relatedSlugs: ['sword-and-magic', 'cultivation-simulator', 'sweet-trap']
  },
  'magic-academy': {
    id: 8,
    title: '魔法学院优等生',
    author: '金泰亨',
    category: '奇幻冒险',
    slug: 'magic-academy',
    cover: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20illustration%20of%20students%20in%20a%20magical%20academy%20with%20floating%20spells%2C%20warm%20golden%20and%20emerald%20tones%2C%20webtoon%20cover%20art%20style%2C%20magical%20atmosphere&width=800&height=1120&seq=manhwa-cover-08&orientation=portrait',
    description: '在赫里奥斯魔法学院，天赋异禀的少年们学习魔法、结交挚友、对抗黑暗势力的精彩校园故事。',
    rating: 9.3,
    chapters: 89,
    views: 3450000,
    updateDate: '2026-07-02',
    tags: ['魔法', '校园', '成长'],
    synopsis: '赫里奥斯魔法学院——大陆最负盛名的魔法学府。少年韩宇辰凭借过人的天赋考入学院，本以为会度过平静的校园生活，却发现学院中隐藏着不为人知的秘密。\n\n古老的禁术、被封印的密室、神秘的失踪事件……宇辰与好友们组成的冒险小队，一步步揭开学院背后的真相。\n\n当黑暗势力逐渐浮出水面，这群年轻的魔法师们必须挺身而出，守护他们的学院与朋友。',
    relatedSlugs: ['sword-and-magic', 'nine-tailed-fox', 'peach-blossom']
  },
  'office-queen': {
    id: 9,
    title: '职场女王',
    author: '申慧珍',
    category: '都市职场',
    slug: 'office-queen',
    cover: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20business%20illustration%20of%20a%20confident%20woman%20in%20modern%20office%20setting%2C%20sleek%20professional%20style%2C%20warm%20neutral%20tones%2C%20webtoon%20cover%20art&width=800&height=1120&seq=manhwa-cover-09&orientation=portrait',
    description: '从底层职员到企业女王，她用智慧和勇气在男性主导的商界闯出一片天。',
    rating: 8.6,
    chapters: 178,
    views: 4230000,
    updateDate: '2026-07-03',
    tags: ['职场', '励志', '女性'],
    synopsis: '徐智恩，一个从地方大学毕业的普通女孩，在大企业中以临时工身份入职。面对职场中的性别歧视与不公，她没有退缩，而是以超强的业务能力和坚韧的意志一步一步向上攀登。\n\n从最底层的行政助理，到带领团队的部门经理，再到公司最年轻的女性高管——她的每一步都充满了挑战与成长。\n\n这是一个关于职场女性奋斗、成长与自我实现的励志故事。',
    relatedSlugs: ['sweet-trap', 'rebirth-business', 'cat-cafe']
  },
  'nine-tailed-fox': {
    id: 10,
    title: '九尾狐传说',
    author: '朴美英',
    category: '奇幻冒险',
    slug: 'nine-tailed-fox',
    cover: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20illustration%20of%20a%20mysterious%20nine%20tailed%20fox%20spirit%20in%20traditional%20Korean%20setting%2C%20ethereal%20mist%2C%20warm%20amber%20and%20crimson%20tones%2C%20webtoon%20cover%20art&width=800&height=1120&seq=manhwa-cover-10&orientation=portrait',
    description: '千年九尾狐化为人形闯入人间，在寻找失落的狐族圣物途中与人类命运交织的奇幻故事。',
    rating: 9.4,
    chapters: 56,
    views: 1890000,
    updateDate: '2026-06-30',
    tags: ['神话', '奇幻', '古风'],
    synopsis: '千年九尾狐白璃为寻找失落的狐族圣物「月灵珠」化为人形，闯入人间。在这个陌生的世界里，她遇到了善良的人类少年李轩。\n\n起初白璃只想利用李轩帮助自己寻找圣物的下落，却在朝夕相处中逐渐对人类世界产生了感情。\n\n然而，猎妖人的出现打破了平静——他们誓要消灭所有妖族。白璃必须在守护狐族与保护人类朋友之间做出抉择。',
    relatedSlugs: ['sword-and-magic', 'peach-blossom', 'cultivation-simulator']
  },
  'apocalypse-survival': {
    id: 11,
    title: '末日求生指南',
    author: '郑宇',
    category: '悬疑惊悚',
    slug: 'apocalypse-survival',
    cover: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20survival%20thriller%20illustration%20of%20survivors%20in%20a%20post%20apocalyptic%20city%2C%20dramatic%20lighting%2C%20dark%20warm%20earth%20tones%2C%20webtoon%20cover%20art%20style%2C%20tense%20atmosphere&width=800&height=1120&seq=manhwa-cover-11&orientation=portrait',
    description: '末日降临，丧尸横行。一群幸存者在废墟中挣扎求生，人性的光辉与黑暗在绝境中交织。',
    rating: 9.0,
    chapters: 112,
    views: 2780000,
    updateDate: '2026-07-02',
    tags: ['末日', '生存', '惊险'],
    synopsis: '一种神秘的病毒席卷全球，感染者化为嗜血的丧尸。首尔一夜之间沦为人间炼狱。\n\n前特种兵金泰浩在混乱中救下了一群幸存者，带领他们在丧尸横行的城市中艰难求生。资源匮乏、同伴背叛、丧尸围城——每一次抉择都关乎生死。\n\n在这场末日浩劫中，比丧尸更可怕的，是人性深处的黑暗。但金泰浩相信，只要还有一线希望，就值得为之战斗。',
    relatedSlugs: ['night-tracker', 'sword-and-magic', 'rebirth-business']
  },
};

function getArticleData(slug: string) {
  if (articleDataMap[slug]) return articleDataMap[slug];
  const found = featuredManhwa.find((m) => m.slug === slug);
  if (found) {
    return {
      ...found,
      synopsis: `${found.description}\n\n这是一部精彩的韩漫作品，由${found.author}创作，属于${found.category}类型。作品以其独特的叙事风格和精美的画风，赢得了广大读者的喜爱。`,
      relatedSlugs: featuredManhwa.filter((m) => m.slug !== slug).slice(0, 3).map((m) => m.slug)
    };
  }
  return {
    ...featuredManhwa[0],
    synopsis: featuredManhwa[0].description,
    relatedSlugs: featuredManhwa.slice(1, 4).map((m) => m.slug)
  };
}

const mockReadingPages = [
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%20with%20beautiful%20illustration%20of%20characters%20in%20dramatic%20scene%2C%20speech%20bubbles%2C%20dynamic%20composition%2C%20warm%20color%20tones%2C%20vertical%20scroll%20format&width=800&height=1200&seq=reading-page-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%20with%20action%20scene%20of%20characters%20fighting%2C%20motion%20lines%2C%20dramatic%20effects%2C%20warm%20amber%20tones%2C%20vertical%20format&width=800&height=1200&seq=reading-page-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%20with%20emotional%20dialogue%20scene%20between%20two%20characters%2C%20close%20up%20expressions%2C%20soft%20lighting%2C%20warm%20tones%2C%20vertical%20format&width=800&height=1200&seq=reading-page-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20comic%20page%20with%20panoramic%20landscape%20scene%20of%20fantasy%20world%2C%20detailed%20backgrounds%2C%20warm%20color%20palette%2C%20vertical%20scroll%20format&width=800&height=1200&seq=reading-page-04&orientation=portrait',
];

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleData(slug || 'sword-and-magic');
  const [nightMode, setNightMode] = useState(false);
  const [fontSize, setFontSize] = useState(1);

  const categorySlug = categoryToSlug[article.category] || 'fantasy';

  usePageMeta({
    title: `${article.title}无删减韩漫下拉式在线阅读_虚空韩漫网`,
    description: `${article.title} - ${article.description} 虚空韩漫网提供无删减下拉式免费阅读，评分${article.rating}，已更新${article.chapters}话。`,
    keywords: `${article.title},${article.category}韩漫,韩漫下拉式阅读,虚空韩漫网,${article.author}`,
    canonicalUrl: `http://jiukuguoji.com/article/${slug || 'sword-and-magic'}`,
    ogType: 'article',
  });

  const relatedManhwa = useMemo(() => {
    const slugs = (article as any).relatedSlugs as string[] | undefined;
    if (slugs && slugs.length > 0) {
      const matched = featuredManhwa.filter((m) => slugs.includes(m.slug));
      if (matched.length > 0) return matched;
    }
    return featuredManhwa.filter((m) => m.slug !== (slug || '')).slice(0, 3);
  }, [article, slug]);

  return (
    <>
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative h-[350px] md:h-[440px] w-full overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top blur-md scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-4 text-center">
            <h1 className="text-xl md:text-4xl font-heading font-black text-white tracking-wide mb-2">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-white/70 mb-4">{article.author} · {article.category}</p>
            <p className="text-xs md:text-sm text-white/60 mb-6">{article.chapters}话 · 评分 {article.rating} · {article.views.toLocaleString()}次阅读</p>
            <button className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-white font-medium text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-bookmark-line"></i></span>
              加入书架
            </button>
          </div>
        </section>

        <Breadcrumb items={[{ name: '首页', path: '/' }, { name: article.category, path: `/category/${categorySlug}` }, { name: article.title }]} />

        {/* Reading Controls */}
        <section className="w-full bg-background-100 py-8">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">章节切换 / 阅读设置</h2>
                <p className="text-xs text-foreground-500 mt-1">自定义你的阅读体验</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setNightMode(!nightMode)}
                  className={`px-4 py-2 text-xs font-medium rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                    nightMode ? 'bg-foreground-900 text-white border-foreground-900' : 'bg-white text-foreground-700 border-background-300 hover:bg-background-50'
                  }`}
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1 align-middle">
                    <i className={nightMode ? 'ri-sun-line' : 'ri-moon-line'}></i>
                  </span>
                  {nightMode ? '日间模式' : '夜间模式'}
                </button>
                <div className="flex items-center gap-1 bg-white border border-background-300 rounded-full px-2 py-1">
                  <button
                    onClick={() => setFontSize(Math.max(0.8, fontSize - 0.1))}
                    className="w-6 h-6 flex items-center justify-center text-xs text-foreground-600 hover:text-foreground-900 cursor-pointer"
                  >
                    A-
                  </button>
                  <span className="text-xs text-foreground-500 min-w-[50px] text-center">字号</span>
                  <button
                    onClick={() => setFontSize(Math.min(1.5, fontSize + 0.1))}
                    className="w-6 h-6 flex items-center justify-center text-xs text-foreground-600 hover:text-foreground-900 cursor-pointer"
                  >
                    A+
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-background-200 p-4 md:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-list-check-2 text-lg"></i>
                  </span>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900">章节列表</h4>
                    <p className="text-xs text-foreground-500">共{article.chapters}话</p>
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {Array.from({ length: 5 }, (_, i) => (
                    <button key={i} className={`w-full text-left px-3 py-1.5 rounded-md text-xs transition-colors whitespace-nowrap cursor-pointer ${i === 0 ? 'bg-primary-100 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-100'}`}>
                      第{article.chapters - i}话 - {article.title}
                    </button>
                  ))}
                  <button className="w-full text-left px-3 py-1.5 rounded-md text-xs text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer">
                    查看更多章节...
                  </button>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-background-200 p-4 md:p-5 flex flex-col items-center justify-center text-center">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-settings-3-line text-lg"></i>
                </span>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">阅读设置</h4>
                <p className="text-xs text-foreground-500">下拉式纵向阅读<br />流畅舒适的沉浸体验</p>
              </div>
              <div className="bg-white rounded-xl border border-background-200 p-4 md:p-5 flex flex-col items-center justify-center text-center">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-contrast-2-line text-lg"></i>
                </span>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">夜间模式</h4>
                <p className="text-xs text-foreground-500">夜间护眼柔光<br />长时间阅读不疲劳</p>
              </div>
            </div>
          </div>
        </section>

        {/* Synopsis & Stats */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-1/2">
                <h3 className="text-lg md:text-2xl font-bold text-foreground-900 leading-snug mb-4">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6 whitespace-pre-line">
                  {(article as any).synopsis || article.description}
                </p>
                <div className="grid grid-cols-3 divide-x divide-background-300">
                  <div className="pr-3 md:pr-5">
                    <div className="text-xl md:text-3xl font-heading font-bold text-accent-500">{article.chapters}</div>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">总章节数</p>
                  </div>
                  <div className="px-3 md:px-5">
                    <div className="text-xl md:text-3xl font-heading font-bold text-accent-500">{article.views.toLocaleString()}</div>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">累计阅读量</p>
                  </div>
                  <div className="pl-3 md:pl-5">
                    <div className="text-xl md:text-3xl font-heading font-bold text-accent-500">{article.rating}</div>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">用户评分</p>
                  </div>
                </div>
              </div>
              <div className="lg:w-1/2">
                <div className="relative min-h-[200px] rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Soft%20pink%20to%20warm%20cream%20abstract%20shapes%20with%20overlapping%20organic%20circles%20and%20soft%20flowing%20gradients%2C%20digital%20minimalist%20art%2C%20gentle%20layering%20effect&width=640&height=400&seq=article-stats-decor&orientation=landscape"
                    alt=""
                    className="w-full h-full object-cover rounded-xl"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reading Area */}
        <section className={`w-full max-w-[800px] mx-auto px-4 md:px-6 py-10 md:py-14 ${nightMode ? 'bg-foreground-950' : ''}`}>
          <div className={`${nightMode ? 'text-foreground-200' : ''}`}>
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-down-line"></i></span>
                下拉阅读 · 第{article.chapters}话 · {article.title}
              </div>
            </div>

            <article className="space-y-6">
              {mockReadingPages.map((pageUrl, idx) => (
                <div key={idx} className="rounded-lg overflow-hidden">
                  <img
                    src={pageUrl}
                    alt={`${article.title} - 第${article.chapters}话 第${idx + 1}页`}
                    className="w-full h-auto"
                    loading={idx === 0 ? 'eager' : 'lazy'}
                    title={`${article.title} - 虚空韩漫网`}
                  />
                </div>
              ))}
            </article>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-background-200">
              <button className="px-5 py-2.5 text-sm font-medium bg-white border border-background-300 text-foreground-700 rounded-full hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer">
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5 align-middle"><i className="ri-arrow-left-line"></i></span>
                上一话
              </button>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">第{article.chapters}话</span>
                <span className="text-xs text-foreground-400">/ 共{article.chapters}话</span>
              </div>
              <button className="px-5 py-2.5 text-sm font-medium bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                下一话
                <span className="w-4 h-4 inline-flex items-center justify-center ml-1.5 align-middle"><i className="ri-arrow-right-line"></i></span>
              </button>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-8 md:mb-10">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">
                猜你喜欢
              </h2>
              <p className="text-sm text-foreground-500 mt-2">
                根据你的阅读偏好推荐
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5" data-product-shop="">
              {relatedManhwa.map((item) => (
                <ManhwaCard key={item.id} {...item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <SchemaOrg type="WebPage" data={{
        url: `http://jiukuguoji.com/article/${slug || 'sword-and-magic'}`,
        name: `${article.title} - 虚空韩漫网`,
        description: article.description,
        isPartOf: { '@type': 'WebSite', name: '虚空韩漫网', url: 'http://jiukuguoji.com/' },
      }} />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        author: { '@type': 'Person', name: article.author },
        datePublished: '2026-06-15',
        dateModified: article.updateDate || '2026-07-03',
        description: article.description,
        publisher: { '@type': 'Organization', name: '虚空韩漫网', url: 'http://jiukuguoji.com/' },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://jiukuguoji.com/' },
          { '@type': 'ListItem', position: 2, name: article.category, item: `http://jiukuguoji.com/category/${categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />
    </>
  );
}